import React, { useState } from "react";
import "./CalendarModal.scss";
// import "./CalendarModalBigImage.scss";
import i18n from "i18n/config";

interface CalendarEvent {
  title: string;
  date: string;
  description: string;
  image: string;
}

interface CalendarModalProps {
  onClose?: () => void;
}

const CalendarModal: React.FC<CalendarModalProps> = ({ onClose }) => {
  const events = i18n.getResourceBundle(
    i18n.language,
    "calendar",
  ) as CalendarEvent[];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % events.length);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + events.length) % events.length);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const goToSlide = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      nextSlide();
    }

    if (touchStart - touchEnd < -75) {
      prevSlide();
    }
  };

  const currentEvent = events[currentSlide];
  const descriptionHtml = (currentEvent.description || "").replace(
    /\n/g,
    "<br />",
  );

  return (
    <div className="calendar-modal">
      <div className="calendar-modal__frame">
        {onClose && (
          <button
            className="calendar-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        )}

        <button
          className="calendar-modal__nav calendar-modal__nav--prev"
          onClick={prevSlide}
          aria-label="Previous slide"
          disabled={isAnimating}
        >
          ‹
        </button>

        <button
          className="calendar-modal__nav calendar-modal__nav--next"
          onClick={nextSlide}
          aria-label="Next slide"
          disabled={isAnimating}
        >
          ›
        </button>

        <div className="calendar-modal__content">
          <div
            className="calendar-modal__slider"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="calendar-modal__slide" key={currentSlide}>
              <div className="calendar-modal__image-container">
                <img
                  src={currentEvent.image}
                  alt={currentEvent.title}
                  className="calendar-modal__image"
                  onError={(e) => {
                    // Fallback to placeholder if image doesn't load
                    (e.target as HTMLImageElement).src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="400"%3E%3Crect fill="%23e0e0e0" width="320" height="400"/%3E%3Ctext fill="%239a9a9a" font-family="Arial" font-size="16" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage Coming Soon%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>

              <div className="calendar-modal__info">
                <h2 className="calendar-modal__title">{currentEvent.title}</h2>
                <p className="calendar-modal__date">{currentEvent.date}</p>
                <div
                  className="calendar-modal__description"
                  dangerouslySetInnerHTML={{
                    __html: descriptionHtml,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="calendar-modal__dots">
            {events.map((_, index) => (
              <button
                key={index}
                className={`calendar-modal__dot ${
                  index === currentSlide ? "calendar-modal__dot--active" : ""
                }`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <div className="calendar-modal__counter">
            {currentSlide + 1} / {events.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarModal;
